'use client';

import { useEffect } from 'react';
import { PhoneCall, PhoneOff, PhoneIncoming } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ActiveCall } from '@/hooks/voice/webphone-types';
import { useRingtone } from './use-ringtone';
import { CustomerCard } from './customer-card';
import { isInternalExtension } from '@/hooks/voice/phone-format';

interface Props {
  call: ActiveCall;
  onAccept: () => void;
  onReject: () => void;
}

export function IncomingCallModal({ call, onAccept, onReject }: Props) {
  // Toca ringtone enquanto o modal esta aberto
  useRingtone(true);

  // Atalhos de teclado: Enter atende, Esc recusa
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') onAccept();
      else if (e.key === 'Escape') onReject();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onAccept, onReject]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="relative w-96 rounded-2xl bg-card border shadow-2xl overflow-hidden">
        {/* Header gradient com pulse */}
        <div className="px-8 pt-10 pb-6 bg-gradient-to-b from-primary/15 to-transparent text-center">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <div className="relative h-20 w-20 rounded-full bg-primary/20 flex items-center justify-center">
                <PhoneIncoming className="h-10 w-10 text-primary" />
              </div>
            </div>
          </div>

          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Chamada recebida
          </p>
          <p className="text-2xl font-semibold">{call.remoteName ?? call.remoteNumber}</p>
          {call.remoteName && (
            <p className="text-sm text-muted-foreground font-mono mt-1">{call.remoteNumber}</p>
          )}
        </div>

        {/* Customer card via ERP lookup — só pra número externo. Ramal interno
            dá match espúrio no IXC e mostra cliente errado. */}
        <div className="px-4 pb-3">
          {isInternalExtension(call.remoteNumber) ? (
            <p className="rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              Ramal interno {call.remoteNumber}
            </p>
          ) : (
            <CustomerCard phone={call.remoteNumber} />
          )}
        </div>

        {/* Botoes grandes Aircall-style */}
        <div className="grid grid-cols-2 gap-3 p-4">
          <Button
            onClick={onReject}
            className="h-14 bg-red-600 hover:bg-red-700 text-white text-base"
          >
            <PhoneOff className="h-5 w-5 mr-2" /> Recusar
          </Button>
          <Button
            onClick={onAccept}
            className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-base"
          >
            <PhoneCall className="h-5 w-5 mr-2" /> Atender
          </Button>
        </div>

        <div className="px-4 pb-3 text-[10px] text-center text-muted-foreground">
          <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">Enter</kbd> atende ·{' '}
          <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">Esc</kbd> recusa
        </div>
      </div>
    </div>
  );
}
